/**
 * Checks if a URL starts with http:// or https:// protocol
 * @param url - The URL string to check
 * @returns The original URL if it starts with http:// or https://, null otherwise
 * @example
 * startsWithHttpOrHttps('https://example.com'); // Returns: 'https://example.com'
 * startsWithHttpOrHttps('http://example.com');  // Returns: 'http://example.com'
 * startsWithHttpOrHttps('example.com');         // Returns: null
 * startsWithHttpOrHttps('/relative/path');      // Returns: null
 */
export function startsWithHttpOrHttps(url: string): string | null {
  const pattern = /^(http|https):\/\//i
  if (pattern.test(url)) {
    return url
  }

  return null
}

/**
 * Adds the domain from STRAPI_URL environment variable to a relative URL
 * @param url - The URL to process (can be relative, absolute, or null/undefined)
 * @returns The complete URL with domain if relative, the original URL if already absolute, or null if input is falsy
 * @example
 * // Assuming STRAPI_URL = 'https://api.example.com'
 * addDomainOnUrl('/api/users');           // Returns: 'https://api.example.com/api/users'
 * addDomainOnUrl('https://other.com/api'); // Returns: 'https://other.com/api'
 * addDomainOnUrl(null);                   // Returns: null
 * addDomainOnUrl('');                     // Returns: null
 */
export function addDomainOnUrl(url: string | undefined | null): string | null {
  const domain = process.env.STRAPI_URL
  const pattern = /^https?:\/\//i

  if (!url || url === undefined) {
    return null
  }

  if (pattern.test(url)) {
    return url
  }

  return `${domain}${url}`
}

/**
 * Returns null if the input string is empty, undefined, or contains only whitespace
 * @param value - The string value to check
 * @returns The original value if it's not empty, null otherwise
 * @example
 * returnNullIfEmptyString('hello');     // Returns: 'hello'
 * returnNullIfEmptyString('');          // Returns: null
 * returnNullIfEmptyString('   ');       // Returns: null
 * returnNullIfEmptyString(undefined);   // Returns: null
 * returnNullIfEmptyString(null);        // Returns: null
 */
export function returnNullIfEmptyString(
  value: string | null | undefined,
): string | null {
  return (value?.trim() === '') || value === undefined ? null : value
}

/**
 * Calculates the distance between two geographic coordinates using the Haversine formula
 * @param coords1 - First coordinate pair as [latitude, longitude]
 * @param coords2 - Second coordinate pair as [latitude, longitude]
 * @returns Distance in meters between the two coordinates
 * @example
 * const distance = haversineDistance([48.8566, 2.3522], [43.2965, 5.3698]);
 * // Returns distance between Paris and Marseille in meters
 * 
 * @remarks
 * Uses the Haversine formula to calculate great-circle distance
 * Returns result in meters (multiplied by 1000)
 * Earth radius used: 6371 km
 */
export function haversineDistance(
  coords1: [number, number],
  coords2: [number, number],
): number {
  const R = 6371
  const [lat1, lon1] = coords1
  const [lat2, lon2] = coords2

  const dLat: number = (lat2 - lat1) * (Math.PI / 180)
  const dLon: number = (lon2 - lon1) * (Math.PI / 180)

  const a: number =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
    Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2)

  const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance: number = R * c

  return distance * 1000
}

/**
 * Extracts the 'tab' parameter from a URL search string
 * @param location - Object containing the search string from window.location
 * @returns The value of the 'tab' parameter, or null if not found or location is invalid
 * @example
 * // With URL: /page?tab=profile&other=value
 * const activeTab = getDefaultActiveTab({ search: '?tab=profile&other=value' });
 * // Returns: 'profile'
 * 
 * // With URL: /page (no search params)
 * const activeTab = getDefaultActiveTab({ search: '' });
 * // Returns: null
 * 
 * @remarks
 * Uses URLSearchParams to parse the search string
 * Returns null for invalid or missing location/search
 */
export function getDefaultActiveTab(location: {
  search: string
}): string | null {
  if (!location || !location.search) {
    return null
  }
  const searchParams = new URLSearchParams(location.search)
  const tab = searchParams?.get('tab')
  return tab
}
